import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import { useAuth } from "@/auth/auth";
import { Btn, Chip, EmptyState, PageHeader, Panel, ProgressBar, ProgressRing } from "@/design/primitives";
import { kindLabel, lifecycleLabel, lifecycleTone, projectTone } from "@/design/project-visual";

/** Seznam projektů řazený podle Attention Score (dle /Design „PROJECTS LIST"). */
export function ProjectsPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const query = useQuery({ queryKey: ["projects"], queryFn: api.projects });

  const projects = query.data?.projects ?? [];
  const pausedCount = projects.filter((p) => p.lifecycle === "PAUSED").length;

  return (
    <div className="af-animate-fade">
      <PageHeader
        title={cs.projects.title}
        kicker="PORTFOLIO"
        lead={cs.projects.subtitle}
        right={
          <>
            <span className="af-mono text-[11px]" style={{ color: "var(--text-tertiary)" }}>
              {projects.length} projektů
              {pausedCount ? ` · ${pausedCount} ${cs.projects.paused}` : ""}
            </span>
            {session?.user.role === "OWNER" || session?.user.role === "MANAGER" ? (
              <Btn
                variant="primary"
                onClick={() => void navigate({ to: "/projects/new" })}
                data-testid="new-project"
              >
                {cs.projects.newProject}
              </Btn>
            ) : null}
          </>
        }
      />

      <div
        className="mt-4 hidden grid-cols-[64px_1fr_170px_260px_1fr_110px] items-center gap-x-4 px-3 pb-2 af-label-dim lg:grid"
      >
        <span>{cs.projects.att}</span>
        <span>{cs.projects.project}</span>
        <span>{cs.projects.status}</span>
        <span>{cs.projects.milestone}</span>
        <span>{cs.projects.running}</span>
        <span />
      </div>

      {projects.length === 0 && !query.isLoading ? (
        <Panel className="mt-4 p-6 lg:mt-0">
          <EmptyState title={cs.projects.emptyTitle} note={cs.projects.emptyBody} />
        </Panel>
      ) : (
        <div className="mt-4 flex flex-col gap-2.5 lg:mt-0">
          {projects.map((p) => {
            const tone = projectTone(p);
            const dimmed = p.lifecycle === "PAUSED" || p.lifecycle === "ARCHIVED";
            return (
              <Panel
                key={p.id}
                tone={p.attentionScore >= 80 && !dimmed ? tone : undefined}
                className="block cursor-pointer px-3 py-3.5 transition-colors hover:bg-[color-mix(in srgb, var(--accent) 4%, transparent)] lg:grid lg:grid-cols-[64px_1fr_170px_260px_1fr_110px] lg:items-center lg:gap-x-4"
                data-testid={`project-row-${p.slug}`}
                style={dimmed ? { opacity: 0.55 } : undefined}
              >
                {/* desktop: buňky tabulky */}
                <div className="hidden lg:contents">
                  <div onClick={() => openProject(p.slug)}>
                    <ProgressRing value={p.attentionScore} size={46} tone={tone} label={p.attentionScore} />
                  </div>
                  <div onClick={() => openProject(p.slug)}>
                    <div className="flex items-baseline gap-2">
                      <span className="af-card-title" style={{ color: "var(--text-primary)" }}>
                        {p.name}
                      </span>
                      {kindLabel[p.kind] ? (
                        <span className="af-label-dim">{kindLabel[p.kind]}</span>
                      ) : null}
                    </div>
                    {p.statusLine ? (
                      <div className="mt-0.5 text-[12px]" style={{ color: statusLineColor(p.attentionScore) }}>
                        {p.statusLine}
                      </div>
                    ) : null}
                  </div>
                  <div onClick={() => openProject(p.slug)}>
                    <Chip tone={lifecycleTone[p.lifecycle]}>{lifecycleLabel[p.lifecycle]}</Chip>
                  </div>
                  <div onClick={() => openProject(p.slug)}>
                    <div className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
                      {cs.common.none}
                    </div>
                    <ProgressBar className="mt-1.5" value={p.health ?? 0} tone={tone} />
                  </div>
                  <div className="min-w-0 text-[12px]" style={{ color: "var(--text-secondary)" }} onClick={() => openProject(p.slug)}>
                    {p.runningTask ? (
                      <Link
                        to="/projects/$slug/tasks/$number"
                        params={{ slug: p.slug, number: String(p.runningTask.number) }}
                        className="block truncate"
                        style={{ color: "var(--accent)" }}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`project-running-${p.slug}`}
                      >
                        #{p.runningTask.number} {p.runningTask.title}
                        {p.runningTask.totalRunning > 1 ? (
                          <span style={{ color: "var(--text-tertiary)" }}>
                            {" "}
                            {moreRunningLabel(p.runningTask.totalRunning - 1)}
                          </span>
                        ) : null}
                      </Link>
                    ) : (
                      cs.common.none
                    )}
                  </div>
                  <Btn onClick={() => openProject(p.slug)}>{cs.common.open}</Btn>
                </div>
                {/* mobil: stacked karta */}
                <div className="flex flex-col gap-2 lg:hidden">
                  <div className="flex items-start justify-between gap-3" onClick={() => openProject(p.slug)}>
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="flex-none">
                        <ProgressRing value={p.attentionScore} size={46} tone={tone} label={p.attentionScore} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-baseline gap-x-2">
                          <span className="af-card-title" style={{ color: "var(--text-primary)" }}>
                            {p.name}
                          </span>
                          {kindLabel[p.kind] ? (
                            <span className="af-label-dim">{kindLabel[p.kind]}</span>
                          ) : null}
                        </div>
                        {p.statusLine ? (
                          <div className="mt-0.5 break-words text-[12px]" style={{ color: statusLineColor(p.attentionScore) }}>
                            {p.statusLine}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex-none">
                      <Chip tone={lifecycleTone[p.lifecycle]}>{lifecycleLabel[p.lifecycle]}</Chip>
                    </div>
                  </div>
                  <div onClick={() => openProject(p.slug)}>
                    <div className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
                      {cs.common.none}
                    </div>
                    <ProgressBar className="mt-1" value={p.health ?? 0} tone={tone} />
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1 text-[12px]" style={{ color: "var(--text-secondary)" }} onClick={() => openProject(p.slug)}>
                      {p.runningTask ? (
                        <Link
                          to="/projects/$slug/tasks/$number"
                          params={{ slug: p.slug, number: String(p.runningTask.number) }}
                          className="block truncate"
                          style={{ color: "var(--accent)" }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          #{p.runningTask.number} {p.runningTask.title}
                          {p.runningTask.totalRunning > 1 ? (
                            <span style={{ color: "var(--text-tertiary)" }}>
                              {" "}
                              {moreRunningLabel(p.runningTask.totalRunning - 1)}
                            </span>
                          ) : null}
                        </Link>
                      ) : (
                        cs.common.none
                      )}
                    </div>
                    <Btn className="flex-none" onClick={() => openProject(p.slug)}>{cs.common.open}</Btn>
                  </div>
                </div>
              </Panel>
            );
          })}
        </div>
      )}
    </div>
  );

  function openProject(slug: string) {
    void navigate({ to: "/projects/$slug", params: { slug } });
  }
}

function moreRunningLabel(more: number): string {
  return `+${more} ${more >= 5 ? "dalších" : "další"}`;
}

function statusLineColor(attention: number): string {
  if (attention >= 90) return "var(--critical)";
  if (attention >= 80) return "var(--warning)";
  return "var(--text-secondary)";
}
